import { useEffect } from 'react';
import { createFileRoute, Outlet, redirect, useNavigate, isRedirect } from '@tanstack/react-router';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
// Layout routes are NOT code-split — they load eagerly on every authenticated page.
// Direct source imports avoid the barrel cascade during initial page load.
import { DialogSpinner } from '@amber-ui/core/src/component/spinner/DialogSpinner';
import {
  GetGrainBinStoragePeriodDocument,
  GetGrainBinDocument,
} from '@amber-ui/core/src/api/graphql/__generated';
import type { GrainBinLoaderData } from '@amber-ui/core/src/router/loaders/grain-bin-loader';
import { resolveActiveStoragePeriod } from '@amber-ui/core/src/router/loaders/grain-bin-loader';
import { useBinScopedStore } from '@amber-ui/core/src/stores/bin-scoped-store';
import { useAuth } from '@amber-ui/core/src/contexts/auth-context';
import { getPreloadQuery } from '@amber-ui/core/src/router/apollo-module';
import { isChunkLoadError, useChunkErrorReload, ChunkErrorFallback } from '../../util/chunk-error';

// Module-level variables track previous bin/acct for Zustand state reset in beforeLoad.
// beforeLoad runs synchronously outside React, so Zustand's getState() (which works
// outside React) is the correct API here. These variables:
// - Reset to `undefined` on HMR (dev-only, harmless — the `!== undefined` guard prevents
//   a spurious reset on the first navigation after HMR)
// - Persist for the app's lifetime in production (layout routes are eagerly loaded, never
//   code-split, never re-evaluated)
// - Accumulate between test calls within the same module (test isolation requires calling
//   beforeLoad with known sequences)
let previousBin: string | undefined;
let previousAcct: string | undefined;

function GrainBinErrorComponent({ error }: { error: Error; reset: () => void }) {
  const navigate = useNavigate();

  const isNotFound = error.message?.includes('NOT_FOUND');
  const isForbidden =
    error.message?.includes('FORBIDDEN') || error.message?.includes('unauthorized');
  const isChunkError = isChunkLoadError(error);

  useChunkErrorReload(isChunkError);

  useEffect(() => {
    if (isNotFound || isForbidden) {
      // Navigate without search params to strip the invalid bin/acct Sqids.
      // Using router navigation preserves the Apollo cache and React state
      // (unlike window.location.replace which destroys both).
      // navigate() returns a Promise (per TanStack Router docs) — catch to
      // prevent unhandled rejection in strict environments.
      void navigate({ to: '/grain_bin/dashboard', search: {}, replace: true }).catch(() => {});
    }
  }, [isNotFound, isForbidden, navigate]);

  if (isChunkError) {
    return <ChunkErrorFallback />;
  }

  if (isNotFound || isForbidden) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <h2>{isNotFound ? 'Bin Not Found' : 'Access Denied'}</h2>
        <p>Redirecting to dashboard...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, textAlign: 'center' }}>
      <h2>Error loading grain bin</h2>
      <p>{error.message}</p>
      <a href="/grain_bin/dashboard">Go to Dashboard</a>
    </div>
  );
}

export const Route = createFileRoute('/_auth/_grain-bin')({
  // Validate search parameters:
  // - `bin`: Sqid-encoded grain bin ID (non-empty alphanumeric string)
  // - `sp`: Sqid-encoded grain_bin_storage_cycle_id (storage period selection)
  // Invalid values (special chars, empty) are stripped to undefined.
  // TanStack Router merges this with the parent _auth route's { acct?: string },
  // so descendant routes see the combined type { acct?: string; bin?: string; sp?: string }.
  validateSearch: (search: Record<string, unknown>): { bin?: string; sp?: string } => ({
    bin:
      typeof search.bin === 'string' && /^[a-zA-Z0-9]{8,}$/.test(search.bin)
        ? search.bin
        : undefined,
    sp:
      typeof search.sp === 'string' && /^[a-zA-Z0-9]{8,}$/.test(search.sp) ? search.sp : undefined,
  }),
  // Reset bin-scoped Zustand state BEFORE the route renders.
  // This runs synchronously before components mount, preventing the "flash of stale state"
  // where old warning banners or fan triggers briefly appear on the wrong bin.
  //
  // NOTE: acct backfill cannot happen here because auth is undefined on first route
  // resolution (before React renders AppInner which injects auth into router context).
  // The backfill is handled in the component via useEffect instead.
  beforeLoad: ({ search, context: { auth } }) => {
    const currentBin = search.bin;
    const currentAcct = search.acct;

    if (previousAcct !== undefined && currentAcct !== previousAcct) {
      useBinScopedStore.getState().resetAll();
    } else if (previousBin !== undefined && currentBin !== previousBin) {
      useBinScopedStore.getState().resetBinState();
    }

    previousBin = currentBin;
    previousAcct = currentAcct;
  },
  // Extract search params the loader depends on. TanStack Router automatically
  // re-runs the loader when these values change (replaces shouldReload).
  // `acct` is included defensively: if the account changes while a bin is selected,
  // the loader re-runs and the API returns FORBIDDEN → errorComponent handles it.
  // `sp` is included so the loader data is available for the storage period selection hook.
  loaderDeps: ({ search }) => ({ bin: search.bin, acct: search.acct, sp: search.sp }),
  loader: async ({ context: { apolloClient }, deps }): Promise<GrainBinLoaderData> => {
    const grain_bin_id = deps.bin;

    if (!grain_bin_id) {
      return { activeStoragePeriod: null, storagePeriods: [], grain_bin_id: null };
    }

    try {
      // Validate the bin exists before fetching storage periods.
      // GetGrainBin throws "Grain Bin not found" for invalid/nonexistent bin IDs,
      // while GetGrainBinStoragePeriod silently returns [] for nonexistent bins.
      // Use cache-first so this is instant when the bin is already cached (normal flow),
      // and only hits the network for direct URL navigation with unknown bin IDs.
      const grainBinResult = await apolloClient.query({
        query: GetGrainBinDocument,
        variables: { grain_bin_id },
        fetchPolicy: 'cache-first',
      });

      // Correct or backfill the `acct` URL param based on the bin's actual account.
      // This handles two cases:
      // 1. acct is MISSING (direct navigation to ?bin=X without acct) → redirect with bin's account
      // 2. acct is WRONG (bin X lives in account Z, but URL says acct=Y) → redirect with bin's account
      // This prevents the dangerous case where the sidebar shows bins from the wrong account.
      //
      // No redirect loop risk: after redirect, loaderDeps returns the corrected acct,
      // the loader re-runs, GetGrainBin cache-hit returns the same binAccountId, and
      // `binAccountId !== deps.acct` is now false → exits without redirecting.
      // The `account_id` field on a GrainBin never changes (bins don't transfer between
      // accounts), so cache-first staleness cannot produce an oscillating value.
      const binAccountId = grainBinResult.data?.grain_bin?.account_id;
      if (binAccountId && binAccountId !== deps.acct) {
        throw redirect({
          to: '.',
          search: { bin: deps.bin, acct: binAccountId, sp: deps.sp },
          replace: true,
        });
      }

      const result = await apolloClient.query({
        query: GetGrainBinStoragePeriodDocument,
        variables: { grain_bin_id },
        // cache-first: on revisit within the same session, this resolves instantly
        // from the cached root query result (populated by the first visit's network
        // fetch). On first visit to a new bin, this still hits the network since
        // GetGrainBin writes storage periods at the GrainBin entity cache path,
        // not at the root query cache path (different cache keys — no automatic
        // cross-query cache sharing without a read policy).
        // The preloadQuery below then reads from cache (this call just populated it).
        fetchPolicy: 'cache-first',
      });

      const periods = result.data?.grain_bin_storage_periods ?? [];
      const activeStoragePeriod = resolveActiveStoragePeriod(periods);

      // Use preloadQuery for storage periods — returns a reactive QueryRef that
      // components can consume via useReadQuery for cache-reactive subscriptions.
      // Uses cache-first because the apolloClient.query() above already fetched
      // from the network and wrote to cache — no second network request needed.
      // The queryRef provides reactive cache subscriptions: when a mutation updates
      // storage periods, components using useReadQuery re-render automatically.
      const preloadQuery = getPreloadQuery();
      const storagePeriodQueryRef = preloadQuery(GetGrainBinStoragePeriodDocument, {
        variables: { grain_bin_id },
        fetchPolicy: 'cache-first',
      });

      return { activeStoragePeriod, storagePeriods: periods, grain_bin_id, storagePeriodQueryRef };
    } catch (error: unknown) {
      // Re-throw redirect objects (e.g. from the acct correction above) —
      // they're not real errors, just TanStack Router's navigation mechanism.
      if (isRedirect(error)) throw error;

      // If the bin doesn't exist (invalid Sqid, deleted bin, wrong account),
      // redirect to the operations view — the user can pick a valid bin from there.
      // Uses AC4's CombinedGraphQLErrors type guard to reliably check extensions.code
      // (NOT_FOUND from KeyNotFoundError, BAD_USER_INPUT from Sqids decode failure).
      if (CombinedGraphQLErrors.is(error)) {
        const errorCode = error.errors[0]?.extensions?.code;
        if (errorCode === 'NOT_FOUND' || errorCode === 'BAD_USER_INPUT') {
          throw redirect({ to: '/grain/operations', search: { acct: deps.acct } });
        }
      }
      const message = error instanceof Error ? error.message || '' : String(error);
      if (message.includes('FORBIDDEN') || message.includes('unauthorized')) {
        throw error;
      }
      console.error('grainBinLoader: failed to fetch storage periods', error);
      return { activeStoragePeriod: null, storagePeriods: [], grain_bin_id };
    }
  },
  // Suspense boundary inside the dashboard component handles all user-visible
  // loading states. The pendingComponent is retained only for the edge case where
  // the loader takes exceptionally long (>5s — network timeout territory).
  // Normal loads (100-400ms) complete before pendingMs elapses, so the user sees
  // only the dashboard's Suspense fallback ("Loading Dashboard..."), not a
  // sequential "Loading..." → "Loading Dashboard..." stutter.
  pendingMs: 5000,
  pendingComponent: () => <DialogSpinner title="Loading..." open />,
  errorComponent: GrainBinErrorComponent,
  component: function GrainBinLayout() {
    const { grain_bin_id } = Route.useLoaderData();
    const { bin, acct } = Route.useSearch();
    const { account_id: sessionAccountId } = useAuth();
    const navigate = useNavigate();

    // Safety net: backfill `acct` if the loader redirect didn't fire (edge case
    // where the loader returned without redirecting — e.g., GetGrainBin returned
    // no account_id due to a partial response). In normal flow, the loader handles
    // acct correction/backfill before the component ever mounts.
    //
    // Uses sessionAccountId as fallback because the only scenario this fires is when
    // the loader's GetGrainBin response had no account_id (broken/partial response).
    // For admin users viewing other accounts' bins, the loader always has account_id
    // from the full GrainBinFragment response and redirects correctly before mount.
    useEffect(() => {
      if (bin && !acct && sessionAccountId) {
        void navigate({
          // oxlint-disable-next-line typescript/no-explicit-any -- TanStack Router's navigate generic overloads make precise typing impractical for search merge
          search: { bin, acct: sessionAccountId } as any,
          replace: true,
        }).catch(() => {});
      }
    }, [bin, acct, sessionAccountId, navigate]);

    return <Outlet />;
  },
});
